import { useCallback, useMemo, useState } from 'react';
import { List } from 'immutable';
import isFunction from 'transmute/isFunction';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { OK } from 'sequences-lib/constants/SubscriptionStatusTypes';
import { cannotEnrollSequenceLearnMore } from 'sequences-lib/lib/links';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Link } from 'foundations-components/transitional/components/Link';
// @ts-expect-error file is untyped
import { addError as addAlertError } from 'customer-data-ui-utilities/alerts/Alerts';
import { denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { useHasScope } from 'crm-activity-creator-data/auth/hooks/useHasScope';
import PopoverActivityButton from '../PopoverActivityButton';
import { useContactRecords } from './hooks/useContactRecords';
import useContactEligibilities from './hooks/useContactEligibilities';
import { NoRecords, getEnrollInSequenceButtonStatus } from './helpers/getEnrollInSequenceButtonStatus';
import { AccessLevelContextProvider } from 'customer-data-properties/accessLevel/AccessLevelContext';
import useEnrollInSequencePopover from './hooks/useEnrollInSequencePopover';
import EnrollInSequenceModal from './modal/EnrollInSequenceModal';
import { useCreateActivityPermissions } from 'crm-activity-creator-data/permission/CreateActivityPermissionsContext';
import { ENROLLED_IN_SEQUENCE } from 'crm-activity-creator-data/interaction/InteractionTypes';
import { CommunicatorLogger } from 'customer-data-tracking/loggers';
import getSource from 'crm-activity-creator-widgets/helpers/getSource';
import { jsx as _jsx } from "react/jsx-runtime";
function CreateEnrollInSequenceButton({
  buttonSize,
  interactionName,
  isOverflow = false,
  objectTypeId,
  onClickActionComplete,
  onOpenModal,
  onCloseModal,
  objectId
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // flag to control if re-checking eligibilities
  const [eligibilityCheckDone, setEligibilityCheckDone] = useState(false);
  const {
    openUpgradeModal
  } = useUpgradeModal('sequences-activity-button');
  const {
    currentUserCanCommunicate
  } = useCreateActivityPermissions();
  const hasSequencesAccess = useHasScope('sequences-read-access');
  const {
    error: contactError,
    data: contactData
  } = useContactRecords({
    objectTypeId,
    objectId
  });
  if (contactError) {
    addAlertError('createActivityButtons.tooltip.sequenceDisabled.fetchContactRecordError');
  }
  const contacts = contactData ? contactData.contacts : List();
  const {
    eligibilities,
    shouldUpdateEligibilityCheckDone
  } = useContactEligibilities(contacts, eligibilityCheckDone, hasSequencesAccess);
  if (shouldUpdateEligibilityCheckDone && !eligibilityCheckDone) {
    setEligibilityCheckDone(true);
  }
  const enrollInSequenceButtonStatus = useMemo(() => getEnrollInSequenceButtonStatus({
    objectTypeId,
    objectId,
    contacts,
    eligibilities
  }), [objectTypeId, objectId, contacts, eligibilities]);
  const handleSequenceUnenroll = useCallback(params => {
    if (params.objectId === objectId) {
      setEligibilityCheckDone(false);
    }
  }, [objectId]);
  useOnCrmMessageTopic(TOPIC_NAMES.SEQUENCE_UNENROLL, handleSequenceUnenroll);
  const handleCloseModal = useCallback(() => {
    if (onCloseModal) {
      onCloseModal();
    }
    setEligibilityCheckDone(false);
  }, [onCloseModal]);
  const handleOpenModal = useCallback(recipient => {
    setIsPopoverOpen(false);
    if (isFunction(onOpenModal) && recipient) {
      const renderedModal = /*#__PURE__*/_jsx(EnrollInSequenceModal, {
        objectType: denormalizeTypeId(objectTypeId),
        objectId: objectId,
        recipient: recipient,
        handleCloseModal: handleCloseModal
      });
      onOpenModal(renderedModal);
      onClickActionComplete({
        targetState: false
      });
    }
  }, [objectTypeId, onClickActionComplete, onOpenModal, objectId, handleCloseModal]);
  const [renderedEnrollInSequencePopover] = useEnrollInSequencePopover({
    onSequenceButtonClick: handleOpenModal,
    contacts,
    eligibilities
  });
  const handlePopoverToggle = targetState => {
    setIsPopoverOpen(targetState);
  };

  // Because this button opens the sequences communicator, it will never trigger the communicator or interaction window.
  const logSequencesButtonClicked = () => {
    CommunicatorLogger.log('communicatorInteraction', {
      action: 'open communicator',
      activity: ENROLLED_IN_SEQUENCE,
      channel: ENROLLED_IN_SEQUENCE,
      source: getSource(isOverflow)
    });
  };
  const handleButtonClick = () => {
    if (!hasSequencesAccess) {
      openUpgradeModal();
      return;
    }
    // We can only directly enroll a contact in a sequence - for other types we need to present a popover with associated contacts instead.
    if (objectTypeId === CONTACT_TYPE_ID) {
      handleOpenModal(objectId);
      onClickActionComplete({
        targetState: false
      });
      logSequencesButtonClicked();
    } else {
      setIsPopoverOpen(!isPopoverOpen);
      onClickActionComplete({
        targetState: true
      });
      logSequencesButtonClicked();
    }
  };
  const isButtonDisabled = !currentUserCanCommunicate || Boolean(enrollInSequenceButtonStatus && enrollInSequenceButtonStatus !== OK);
  const learnMoreLink = /*#__PURE__*/_jsx(Link, {
    href: cannotEnrollSequenceLearnMore(),
    external: true,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "createActivityButtons.tooltip.sequenceDisabled.learnMoreText"
    })
  });
  const tooltipMessage = isButtonDisabled ? !currentUserCanCommunicate ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "createActivityButtons.tooltip.disabled"
  }) : enrollInSequenceButtonStatus ? /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: `createActivityButtons.tooltip.sequenceDisabled.${enrollInSequenceButtonStatus.toLowerCase()}`,
    options: {
      learnMoreLink
    }
  }) : learnMoreLink : undefined;
  const showLoadingState = hasSequencesAccess && (!currentUserCanCommunicate || enrollInSequenceButtonStatus === NoRecords ? false : !eligibilityCheckDone);
  return /*#__PURE__*/_jsx(AccessLevelContextProvider, {
    children: /*#__PURE__*/_jsx(PopoverActivityButton, {
      buttonSize: buttonSize,
      buttonProps: {
        onClick: handleButtonClick
      },
      dataTestId: "create-enrolled-in-sequence-button",
      disabled: isButtonDisabled,
      interactionName: interactionName,
      isLoading: showLoadingState,
      isOverflow: isOverflow,
      onboardingSelector: "create-engagement-enrolled-in-sequence",
      seleniumSelector: "create-engagement-enrolled-in-sequence-button",
      tooltipMessage: tooltipMessage,
      objectTypeId: objectTypeId,
      objectId: objectId,
      content: renderedEnrollInSequencePopover,
      onPopoverToggle: handlePopoverToggle,
      open: isPopoverOpen
    })
  });
}
export default CreateEnrollInSequenceButton;